import { supabase, supabaseAnon } from '../lib/supabase'
import { sortEventSessionsByStartTime } from '../utils/sessionTime'
import { BUCKET_EVENT_IMAGES, getPublicImageUrl } from '../utils/storage'
import {
  applyFilters,
  applyOrdering,
  applyRange,
  applyRoleConstraints,
  applySearch,
  applyOwnershipToPayload,
  handleQuery,
  formatSupabaseError,
  accessDeniedResponse,
  resolveRoleContext,
  ensureCanManageEvent
} from './utils'

const TABLE_NAME = 'events'

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

/** Drop legacy location columns removed from schema (location → location_name, location_details dropped). */
const stripLegacyEventLocationFields = (payload) => {
  if (!payload || typeof payload !== 'object') return payload
  const { location, location_details, ...rest } = payload
  return rest
}

/**
 * Format user input into a URL-safe slug (lowercase, hyphens, no spaces/special chars).
 * @param {string} input
 * @returns {string}
 */
export const formatSlug = (input) => {
  if (input == null || typeof input !== 'string') return ''
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
}

/**
 * Validate slug format (URL-safe, non-empty).
 * @param {string} slug
 * @returns {{ valid: boolean, message?: string }}
 */
export const validateSlugFormat = (slug) => {
  if (!slug || typeof slug !== 'string') {
    return { valid: false, message: 'Slug is required.' }
  }
  const trimmed = slug.trim()
  if (!trimmed) {
    return { valid: false, message: 'Slug is required.' }
  }
  if (!SLUG_PATTERN.test(trimmed)) {
    return { valid: false, message: 'Slug can only contain lowercase letters, numbers, and hyphens.' }
  }
  return { valid: true }
}

/**
 * Check if a slug is available (not used by another event).
 * @param {string} slug
 * @param {string} [excludeEventId] - event id to exclude (for updates)
 * @returns {Promise<{ data: { available: boolean } | null, error: object | null }>}
 */
export const checkSlugAvailability = async (slug, excludeEventId = null) => {
  if (!slug || !slug.trim()) {
    return { data: { available: false }, error: null }
  }
  const normalizedSlug = formatSlug(slug) || String(slug).trim().toLowerCase()
  let query = supabase
    .from(TABLE_NAME)
    .select('id')
    .eq('event_slug', normalizedSlug)
    .limit(1)
  if (excludeEventId) {
    query = query.neq('id', excludeEventId)
  }
  const { data, error } = await query.maybeSingle()
  if (error) {
    return { data: null, error }
  }
  return { data: { available: !data }, error: null }
}

/**
 * Fetch events with optional filtering, search, and role constraints.
 * @param {{
 *   role?: 'student' | 'agent' | 'partner' | 'admin',
 *   userId?: string,
 *   filters?: Array<{ column: string, operator: string, value: any }>,
 *   search?: string,
 *   searchColumns?: string[],
 *   orderBy?: { column: string, ascending?: boolean },
 *   range?: { from: number, to: number },
 *   studentEventIds?: string[],
 *   partnerEventIds?: string[]
 * }} [options]
 */
export const listEvents = async (options = {}) => {
  const {
    role,
    userId,
    filters,
    search,
    searchColumns = ['title', 'description', 'location_name', 'location_address'],
    orderBy = { column: 'created_at', ascending: false },
    range,
    studentEventIds,
    partnerEventIds
  } = options

  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const resolvedRole = roleContext.role
  const resolvedUserId = roleContext.userId

  if (resolvedRole === 'student') {
    if (studentEventIds === undefined || studentEventIds === null) {
      return accessDeniedResponse('Students can only access their registered events.')
    }
    if (studentEventIds.length === 0) {
      return { data: [], error: null }
    }
  }

  if (resolvedRole === 'partner' && (!partnerEventIds || partnerEventIds.length === 0)) {
    return accessDeniedResponse('Partners can only access their assigned events.')
  }

  let query = supabase.from(TABLE_NAME).select('*')

  query = applyRoleConstraints(query, {
    role: resolvedRole,
    userId: resolvedUserId,
    ownershipField: 'created_by',
    agentField: 'agent_id',
    studentField: 'student_id',
    studentEventIds,
    partnerEventIds,
    eventIdField: 'id'
  })

  query = applyFilters(query, filters)
  query = applySearch(query, search, searchColumns)
  query = applyOrdering(query, orderBy)
  query = applyRange(query, range)

  return handleQuery(query, { fallbackMessage: 'Unable to load events.' })
}

/**
 * List promotable events: events the current user is allowed to see under RLS (e.g. status = 'public').
 * Does not filter by agent_id; RLS policies enforce visibility. Used by the Invitation Links page so agents
 * can create share links for any event they are allowed to promote.
 *
 * @param {{
 *   role?: 'student' | 'agent' | 'partner' | 'admin',
 *   userId?: string,
 *   orderBy?: { column: string, ascending?: boolean },
 *   range?: { from: number, to: number }
 * }} [options]
 */
export const listPromotableEvents = async (options = {}) => {
  const {
    role,
    userId,
    orderBy = { column: 'created_at', ascending: false },
    range
  } = options

  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const resolvedRole = roleContext.role

  if (resolvedRole === 'student') {
    return accessDeniedResponse('Students cannot list promotable events.')
  }

  let query = supabase
    .from(TABLE_NAME)
    .select('*')
    .eq('status', 'public')

  query = applyOrdering(query, orderBy)
  query = applyRange(query, range)

  return handleQuery(query, { fallbackMessage: 'Unable to load promotable events.' })
}

/**
 * Fetch a single public event by slug. No auth or role context; relies on RLS
 * allowing SELECT where status = 'public'. Used by slug route and share-link resolution.
 *
 * @param {string} eventSlug
 * @returns {Promise<{ data: object | null, error: object | null }>}
 */
/**
 * Fetch a single public event by slug (case-insensitive).
 * Uses RPC get_public_event_by_slug for case-insensitive matching so /FinPlan and /finplan both work.
 */
export const getPublicEventBySlug = async (eventSlug) => {
  if (!eventSlug) {
    return { data: null, error: { message: 'Event slug is required.', code: 'invalid_input' } }
  }

  const slug = String(eventSlug).trim()
  // Use anon client to avoid auth refresh blocking public requests (CORS/522 cascade)
  const { data: rows, error } = await supabaseAnon.rpc('get_public_event_by_slug', { p_slug: slug })

  if (error) {
    return { data: null, error }
  }
  const data = Array.isArray(rows) && rows.length > 0 ? rows[0] : null
  return { data, error: data ? null : { message: 'Unable to load the event.', code: 'not_found' } }
}

/**
 * Fetch a single public event by id. No auth or role context; relies on RLS
 * allowing SELECT where status = 'public'. Used by /register/:eventId and /landing/:eventId.
 *
 * @param {string} eventId
 * @returns {Promise<{ data: object | null, error: object | null }>}
 */
export const getPublicEventById = async (eventId) => {
  if (!eventId) {
    return { data: null, error: { message: 'Event id is required.', code: 'invalid_input' } }
  }

  // Use anon client to avoid auth refresh blocking public requests
  const query = supabaseAnon
    .from(TABLE_NAME)
    .select('*')
    .eq('id', eventId)
    .eq('status', 'public')
    .maybeSingle()

  return handleQuery(query, {
    fallbackMessage: 'Unable to load the event.',
    allowEmpty: false
  })
}

/**
 * List public events for student dashboard (recommended, featured, other).
 * No role required; RLS allows SELECT where status = 'public'.
 *
 * @param {{ featured?: boolean, limit?: number, orderBy?: { column: string, ascending?: boolean } }} [filters]
 * @returns {Promise<{ data: Array<object>, error: object | null }>}
 */
export const getPublicEvents = async (filters = {}) => {
  const { limit = 20, orderBy = { column: 'created_at', ascending: false } } = filters
  let query = supabase
    .from(TABLE_NAME)
    .select('*')
    .eq('status', 'public')
  query = query.order(orderBy.column, { ascending: orderBy.ascending ?? true })
  if (limit > 0) query = query.limit(limit)
  return handleQuery(query, {
    fallbackMessage: 'Unable to load events.',
    allowEmpty: true
  })
}

/**
 * Fetch a single event by id with role constraints.
 * @param {string} eventId
 * @param {{
 *   role?: 'student' | 'agent' | 'partner' | 'admin',
 *   userId?: string,
 *   studentEventIds?: string[],
 *   partnerEventIds?: string[]
 * }} [options]
 */
export const getEventById = async (eventId, options = {}) => {
  const { role, userId, studentEventIds, partnerEventIds } = options

  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const resolvedRole = roleContext.role
  const resolvedUserId = roleContext.userId

  if (resolvedRole === 'student' && (!studentEventIds || studentEventIds.length === 0)) {
    return accessDeniedResponse('Students can only access their registered events.')
  }

  if (resolvedRole === 'partner') {
    if (partnerEventIds && partnerEventIds.length > 0) {
      // handled by role constraints below
    } else {
      const access = await ensureCanManageEvent(eventId)
      if (!access.allowed) {
        return { data: null, error: access.error }
      }
    }
  }

  if (resolvedRole === 'agent') {
    const access = await ensureCanManageEvent(eventId)
    if (!access.allowed) {
      return { data: null, error: access.error }
    }
  }

  let query = supabase.from(TABLE_NAME).select('*').eq('id', eventId).maybeSingle()

  query = applyRoleConstraints(query, {
    role: resolvedRole,
    userId: resolvedUserId,
    ownershipField: 'created_by',
    agentField: 'agent_id',
    studentField: 'student_id',
    studentEventIds,
    partnerEventIds,
    eventIdField: 'id'
  })

  return handleQuery(query, {
    fallbackMessage: 'Unable to load the event.',
    allowEmpty: false
  })
}

/**
 * Create a new event.
 * Expects event_slug in payload; validated for format and uniqueness.
 * @param {Record<string, any>} payload
 * @param {{ role?: 'student' | 'agent' | 'partner' | 'admin', userId?: string }} [options]
 */
export const createEvent = async (payload, options = {}) => {
  const { role, userId } = options
  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const resolvedRole = roleContext.role
  if (resolvedRole === 'student' || resolvedRole === 'partner') {
    return accessDeniedResponse('You do not have permission to create events.')
  }

  const rawSlug = payload.event_slug != null ? String(payload.event_slug).trim() : ''
  const slug = formatSlug(rawSlug) || formatSlug(payload.title) || rawSlug
  const formatCheck = validateSlugFormat(slug)
  if (!formatCheck.valid) {
    return { data: null, error: { message: formatCheck.message, code: 'invalid_input' } }
  }
  const { data: availability } = await checkSlugAvailability(slug)
  if (availability && !availability.available) {
    return { data: null, error: { message: 'This slug is already in use. Please choose another.', code: 'duplicate_slug' } }
  }

  const { event_slug: _drop, ...rest } = stripLegacyEventLocationFields(payload)
  const data = applyOwnershipToPayload(
    { ...rest, event_slug: slug },
    {
      role: resolvedRole,
      userId: roleContext.userId,
      ownershipField: 'created_by',
      agentField: 'agent_id'
    }
  )

  const query = supabase.from(TABLE_NAME).insert(data).select('*').single()

  return handleQuery(query, {
    fallbackMessage: 'Unable to create the event.',
    allowEmpty: false
  })
}

/**
 * Update an event by id.
 * When updates.event_slug is provided, it is validated for format and uniqueness (excluding this event).
 * @param {string} eventId
 * @param {Record<string, any>} updates
 * @param {{ role?: 'student' | 'agent' | 'partner' | 'admin', userId?: string }} [options]
 */
export const updateEvent = async (eventId, updates, options = {}) => {
  const { role, userId } = options
  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const resolvedRole = roleContext.role
  const resolvedUserId = roleContext.userId

  if (resolvedRole === 'student') {
    return accessDeniedResponse('Students cannot update events.')
  }

  if (resolvedRole === 'agent' || resolvedRole === 'partner') {
    const access = await ensureCanManageEvent(eventId)
    if (!access.allowed) {
      return { data: null, error: access.error }
    }
  }

  let finalUpdates = stripLegacyEventLocationFields({ ...updates })
  if (updates.event_slug !== undefined) {
    const slug = formatSlug(updates.event_slug) || String(updates.event_slug).trim()
    const formatCheck = validateSlugFormat(slug)
    if (!formatCheck.valid) {
      return { data: null, error: { message: formatCheck.message, code: 'invalid_input' } }
    }
    const { data: availability } = await checkSlugAvailability(slug, eventId)
    if (availability && !availability.available) {
      return { data: null, error: { message: 'This slug is already in use. Please choose another.', code: 'duplicate_slug' } }
    }
    finalUpdates = { ...finalUpdates, event_slug: slug }
  }

  let query = supabase.from(TABLE_NAME).update(finalUpdates).eq('id', eventId)

  query = applyRoleConstraints(query, {
    role: resolvedRole,
    userId: resolvedUserId,
    ownershipField: 'created_by',
    agentField: 'agent_id'
  })

  query = query.select('*').single()

  return handleQuery(query, {
    fallbackMessage: 'Unable to update the event.',
    allowEmpty: false
  })
}

/**
 * Delete an event by id.
 * @param {string} eventId
 * @param {{ role?: 'student' | 'agent' | 'partner' | 'admin', userId?: string }} [options]
 */
export const deleteEvent = async (eventId, options = {}) => {
  const { role, userId } = options
  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const resolvedRole = roleContext.role
  const resolvedUserId = roleContext.userId

  if (resolvedRole === 'student') {
    return accessDeniedResponse('Students cannot delete events.')
  }

  if (resolvedRole === 'agent' || resolvedRole === 'partner') {
    const access = await ensureCanManageEvent(eventId)
    if (!access.allowed) {
      return { data: null, error: access.error }
    }
  }

  let query = supabase.from(TABLE_NAME).delete().eq('id', eventId)

  query = applyRoleConstraints(query, {
    role: resolvedRole,
    userId: resolvedUserId,
    ownershipField: 'created_by',
    agentField: 'agent_id'
  })

  query = query.select('*').single()

  return handleQuery(query, {
    fallbackMessage: 'Unable to delete the event.',
    allowEmpty: false
  })
}

/**
 * Duplicate an event and selected related records via RPC.
 * @param {string} eventId
 * @param {{ role?: 'student' | 'agent' | 'partner' | 'admin', userId?: string }} [options]
 */
export const duplicateEvent = async (eventId, options = {}) => {
  const { role, userId } = options
  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const resolvedRole = roleContext.role
  if (resolvedRole === 'student' || resolvedRole === 'partner') {
    return accessDeniedResponse('You do not have permission to duplicate events.')
  }

  const { data, error } = await supabase.rpc('duplicate_event', { p_event_id: eventId })

  if (error) {
    return { data: null, error }
  }

  return { data, error: null }
}

/**
 * Get event with all related data (sessions, pricing, etc.)
 * @param {string} eventId
 * @param {{ role?: 'student' | 'agent' | 'partner' | 'admin', userId?: string }} [options]
 */
export const getEventWithDetails = async (eventId, options = {}) => {
  const { role, userId } = options
  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const resolvedRole = roleContext.role

  if (resolvedRole === 'student') {
    return accessDeniedResponse('Students cannot access event details for editing.')
  }

  if (resolvedRole === 'agent' || resolvedRole === 'partner') {
    const access = await ensureCanManageEvent(eventId)
    if (!access.allowed) {
      return { data: null, error: access.error }
    }
  }

  try {
    // Get main event data with sessions and pricing.
    // Use !event_id to disambiguate: events has two FKs to event_sessions (event_sessions.event_id and events.orientation_session_id).
    const { data: eventData, error: eventError } = await supabase
      .from(TABLE_NAME)
      .select(`
        *,
        sessions:event_sessions!event_id(*)
      `)
      .eq('id', eventId)
      .single()

    if (eventError) {
      return { data: null, error: eventError }
    }

    // Get instructors separately using RPC function
    const { data: instructors, error: instructorsError } = await supabase
      .rpc('get_event_instructors', { p_event_id: eventId })

    if (instructorsError) {
      console.warn('[EventsService] Error fetching instructors:', instructorsError)
      // Continue without instructors - don't fail the whole request
    }

    const sessions = Array.isArray(eventData.sessions)
      ? sortEventSessionsByStartTime(eventData.sessions)
      : eventData.sessions

    return {
      data: {
        ...eventData,
        sessions,
        instructors: instructors || []
      },
      error: null
    }
  } catch (error) {
    console.error('[EventsService] getEventWithDetails error:', error)
    return { data: null, error }
  }
}

/**
 * Update event with extended fields
 * @param {string} eventId
 * @param {Record<string, any>} updates
 * @param {{ role?: 'student' | 'agent' | 'partner' | 'admin', userId?: string }} [options]
 */
export const updateEventExtended = async (eventId, updates, options = {}) => {
  const { role, userId } = options
  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const resolvedRole = roleContext.role
  const resolvedUserId = roleContext.userId

  if (resolvedRole === 'student') {
    return accessDeniedResponse('Students cannot update events.')
  }

  if (resolvedRole === 'agent' || resolvedRole === 'partner') {
    const access = await ensureCanManageEvent(eventId)
    if (!access.allowed) {
      return { data: null, error: access.error }
    }
  }

  // Remove nested objects that shouldn't be directly updated on events table
  const { sessions, instructors, pricing, ...eventUpdates } = updates

  let finalUpdates = { ...stripLegacyEventLocationFields(eventUpdates), updated_at: new Date().toISOString() }
  if (eventUpdates.event_slug !== undefined) {
    const slug = formatSlug(eventUpdates.event_slug) || String(eventUpdates.event_slug).trim()
    const formatCheck = validateSlugFormat(slug)
    if (!formatCheck.valid) {
      return { data: null, error: { message: formatCheck.message, code: 'invalid_input' } }
    }
    const { data: availability } = await checkSlugAvailability(slug, eventId)
    if (availability && !availability.available) {
      return { data: null, error: { message: 'This slug is already in use. Please choose another.', code: 'duplicate_slug' } }
    }
    finalUpdates = { ...finalUpdates, event_slug: slug }
  }

  let query = supabase
    .from(TABLE_NAME)
    .update(finalUpdates)
    .eq('id', eventId)

  query = applyRoleConstraints(query, {
    role: resolvedRole,
    userId: resolvedUserId,
    ownershipField: 'created_by',
    agentField: 'agent_id'
  })

  query = query.select('*').single()

  return handleQuery(query, {
    fallbackMessage: 'Unable to update the event.',
    allowEmpty: false
  })
}

/**
 * Get event sessions
 * @param {string} eventId
 */
export const getEventSessions = async (eventId) => {
  const query = supabase
    .from('event_sessions')
    .select('*')
    .eq('event_id', eventId)
    .order('start_time', { ascending: true })

  return handleQuery(query, {
    fallbackMessage: 'Unable to load event sessions.'
  })
}

/**
 * Get event sessions for public landing pages (no auth required).
 * Uses supabaseAnon to avoid session refresh issues on mobile/custom domains.
 * RLS on event_sessions allows SELECT for public events.
 * @param {string} eventId
 */
export const getPublicEventSessions = async (eventId) => {
  const query = supabaseAnon
    .from('event_sessions')
    .select('*')
    .eq('event_id', eventId)
    .order('start_time', { ascending: true })

  return handleQuery(query, {
    fallbackMessage: 'Unable to load event sessions.'
  })
}

/**
 * Get event with full details and student progress (for student event details page).
 * Student must be registered for the event (enforced by RLS on related data).
 *
 * @param {string} eventId
 * @param {string} studentId
 * @param {{ role?: string, userId?: string }} [options]
 * @returns {Promise<{ data: object | null, error: object | null }>}
 */
export const getEventWithProgress = async (eventId, studentId, options = {}) => {
  const roleContext = await resolveRoleContext(options)
  if (roleContext.error) return { data: null, error: roleContext.error }

  if (roleContext.role === 'student' && roleContext.userId !== studentId) {
    return accessDeniedResponse('You can only view your own event progress.')
  }

  const { data: event, error: eventError } = await supabase
    .from(TABLE_NAME)
    .select('*')
    .eq('id', eventId)
    .single()

  if (eventError || !event) {
    return {
      data: null,
      error: eventError ? formatSupabaseError(eventError, 'Unable to load event.') : { message: 'Event not found.', code: 'not_found' }
    }
  }

  const [sessionsRes, instructorsRes, resourcesRes, objectivesRes, homeworkRes, progressRes, sessionProgressRes] = await Promise.all([
    getEventSessions(eventId),
    supabase.rpc('get_event_instructors', { p_event_id: eventId }).then((r) => ({ data: r.data || [], error: r.error })),
    supabase.from('event_resources').select('*').eq('event_id', eventId).eq('published', true).order('created_at', { ascending: true }),
    supabase.from('event_learning_objectives').select('*').eq('event_id', eventId).order('display_order', { ascending: true }),
    import('./homeworkService').then((m) => m.listEventHomework(eventId, options)),
    import('./progressService').then((m) => m.getResourceProgress(studentId, eventId, options)),
    import('./progressService').then((m) => m.getSessionProgress(studentId, eventId, options))
  ])

  const progressService = await import('./progressService')
  const progressPct = await progressService.calculateEventProgress(studentId, eventId, options)

  return {
    data: {
      ...event,
      sessions: sessionsRes.data ?? [],
      instructors: instructorsRes.data ?? [],
      resources: resourcesRes.data ?? [],
      learningObjectives: objectivesRes.data ?? [],
      homework: homeworkRes.data ?? [],
      resourceProgress: (progressRes.data ?? []).reduce((acc, p) => {
        acc[p.resource_id] = p
        return acc
      }, {}),
      sessionProgress: (sessionProgressRes.data ?? []).reduce((acc, p) => {
        acc[p.session_id] = p
        return acc
      }, {}),
      progressPercentage: progressPct.data?.percentage ?? 0,
      completedResources: progressPct.data?.completedResources ?? 0,
      totalResources: progressPct.data?.totalResources ?? 0
    },
    error: null
  }
}

/**
 * Create event session
 * @param {Record<string, any>} sessionData
 */
export const createEventSession = async (sessionData) => {
  const { location: _legacyLocation, ...sessionPayload } = sessionData || {}
  const query = supabase
    .from('event_sessions')
    .insert([{
      ...sessionPayload,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }])
    .select()
    .single()

  return handleQuery(query, {
    fallbackMessage: 'Unable to create event session.',
    allowEmpty: false
  })
}

/**
 * Update event session
 * @param {string} sessionId
 * @param {Record<string, any>} updates
 */
export const updateEventSession = async (sessionId, updates) => {
  const { location: _legacyLocation, ...sessionUpdates } = updates || {}
  const query = supabase
    .from('event_sessions')
    .update({
      ...sessionUpdates,
      updated_at: new Date().toISOString()
    })
    .eq('id', sessionId)
    .select()
    .single()

  return handleQuery(query, {
    fallbackMessage: 'Unable to update event session.',
    allowEmpty: false
  })
}

/**
 * Delete event session
 * @param {string} sessionId
 */
export const deleteEventSession = async (sessionId) => {
  const query = supabase
    .from('event_sessions')
    .delete()
    .eq('id', sessionId)
    .select()
    .single()

  return handleQuery(query, {
    fallbackMessage: 'Unable to delete event session.',
    allowEmpty: false
  })
}

/**
 * Create a new event from a template. Pre-fills title, description, and optionally
 * copies sessions. New event respects agent_id = auth.uid and event RLS.
 * @param {string} templateId
 * @param {{ role?: 'student' | 'agent' | 'partner' | 'admin', userId?: string }} [options]
 */
export const createEventFromTemplate = async (templateId, options = {}) => {
  const { role, userId } = options
  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const resolvedRole = roleContext.role
  const resolvedUserId = roleContext.userId

  if (resolvedRole === 'student' || resolvedRole === 'partner') {
    return accessDeniedResponse('You do not have permission to create events from templates.')
  }

  // Dynamic import to avoid circular dependency
  const templatesService = await import('./templatesService')
  const { data: templateData, error: templateError } = await templatesService.getTemplateById(templateId, { role: resolvedRole, userId: resolvedUserId })

  if (templateError || !templateData) {
    return { data: null, error: templateError || { message: 'Template not found.', code: 'not_found' } }
  }

  const template = templateData

  const eventPayload = {
    title: template.name ?? template.title ?? 'Untitled Event',
    description: '',
    status: 'draft',
    agent_id: resolvedUserId,
    created_by: resolvedUserId,
  }

  const { data: newEvent, error: createError } = await createEvent(eventPayload, { role: resolvedRole, userId: resolvedUserId })

  if (createError || !newEvent) {
    return { data: null, error: createError }
  }

  return { data: newEvent, error: null }
}

/**
 * Upload event featured image to Supabase Storage (event-images bucket).
 * Path: {eventId}/{uniqueFilename}
 * @param {string} eventId
 * @param {File} file
 * @returns {Promise<{ data: { path: string, publicUrl: string | null, fullPath?: string } | null, error: object | null }>}
 */
export const uploadEventFeaturedImage = async (eventId, file) => {
  try {
    if (!eventId || !file) {
      return {
        data: null,
        error: { message: 'Event ID and file are required.', code: 'validation' },
      }
    }

    const access = await ensureCanManageEvent(eventId)
    if (!access.allowed) {
      return { data: null, error: access.error }
    }

    const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg'
    const path = `${eventId}/${crypto.randomUUID()}.${ext}`

    const { data: uploadData, error: uploadError } = await supabase.storage
      .from(BUCKET_EVENT_IMAGES)
      .upload(path, file, { upsert: true, contentType: file.type })

    if (uploadError) throw uploadError

    const publicUrl = getPublicImageUrl(uploadData.path, BUCKET_EVENT_IMAGES)

    return {
      data: {
        path: uploadData.path,
        publicUrl,
        fullPath: uploadData.fullPath ?? path,
      },
      error: null,
    }
  } catch (error) {
    console.error('[EventsService] uploadEventFeaturedImage error:', error)
    return {
      data: null,
      error: formatSupabaseError(error, 'Failed to upload featured image.'),
    }
  }
}
